import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.css";
import "@fortawesome/fontawesome-free/js/all.js";

import { ReactKeycloakProvider } from '@react-keycloak/web'
import keycloak from "./Keycloak.js";


import "./App-style.css";
import 'semantic-ui-css/semantic.min.css';
 
//Import des pages
import Home from './Pages/Home';
//import Compromised from './Pages/Compromised';

import {IntlProvider} from 'react-intl'
import French from './Lang/fr.json';
import English from './Lang/en.json';
import Spanish from './Lang/es.json';

import SecuredPage from "./Components/Login/SecuredPage";
import PrivateRoute from "./Components/Login/PrivateRoute";

import DataServiceSQL from "./DataService/DataServiceSQL.js";
import DisclaimerModal from "./Components/Common/DisclaimerModal.js";
import UserAccountGate from "./Components/Common/UserAccountGate.js";

import useScreenSize from "./Components/Common/useScreenSize.js";

// Stable reference (module scope) so <IntlProvider> doesn't recreate the `intl`
// object on every App re-render. An inline onError would change identity each
// render, forcing react-intl to rebuild `intl`, which re-runs every effect/memo
// keyed on it (e.g. FixturesMenu's catalog load resetting to the first tab).
const handleIntlError = (err) => {
	// Silently ignore missing translation warnings (keys without a defaultMessage
	// or not yet translated); surface any other intl errors.
	if (err && err.code === 'MISSING_TRANSLATION') return;
	console.error(err);
};

const App = () => {

    const screenSize = useScreenSize();

 	const [searchParams, setSearchParams] = useSearchParams();


	const [currentFilter, setCurrentFilter] = useState(getInitialFilter());
	const [currentView, setCurrentView] = useState(getInitialView());
	const [currentLocale, setCurrentLocale] = useState(navigator.language || "en-GB");
	const [currentAlert, setCurrentAlert] = useState(getInitialAlert());
	const [backendHealth, setBackendHealth] = useState({ checked: false, ok: true, message: "" });
	const [showDisclaimer, setShowDisclaimer] = useState(false);
	const [disclaimerUsername, setDisclaimerUsername] = useState(null);

	const historyObj = useNavigate();

	
	// Filter
	function getInitialFilter() {
		
		// Check if url has param
		if (searchParams.has('id') && searchParams.has('type')) {
			return '{"id":"'+ searchParams.get("id") +'", "type":"'+ searchParams.get("type") +'"}'	// TMP: not recognized by the select (show empty)
		}

		// else check if locale storage exist
		let storageVal = localStorage.getItem("filter")
		if(storageVal){
			return storageVal
		}

		// else return all patients
		return '{"id": "null", "type": "group"}'		// TMP: not recognized by the select (show empty)
	}

	async function getInitialAlert() {
		if (searchParams.has('alert')) {
		    await (DataServiceSQL.validateAlert(searchParams.get("alert")))
	    	 	.then((response) => {
	    	 		null
		      	})
	    	  	.catch((e) => {
	        		console.log(e);
		      	});
		}
		return null
	}

	const handleFilterChange = async (e) => {

    	// Delete url param if any
	    if (searchParams.has('id') && searchParams.has('type')) {
	      searchParams.delete('id');
  	      searchParams.delete('type');
	      setSearchParams(searchParams);
	    }

    	// Set new filter selection
    	setCurrentFilter(e.target.value);
    	
    	// Save filter selection
  		localStorage.setItem("filter", e.target.value);
 	};
	
	// View
	function getInitialView() {
	  const savedLocale = localStorage.getItem("view");
	  if (savedLocale === "0") return "3";
	  return savedLocale || "3"
	}

	const handleViewChange = async (e) => {
    	setCurrentView(e.target.value);
  		localStorage.setItem("view", e.target.value);
 	};	
	
	// The connected user (locale + disclaimer state) is loaded by <UserAccountGate>,
	// which lives inside <ReactKeycloakProvider> below so it can wait for the token
	// to be available. Loading it here on App mount fired before login completed
	// (token cookie not yet saved), which is why the disclaimer popup only showed
	// after an F5 refresh and never right after login.
	
	const handleLocaleChange = async (id, e) => {
		if (currentLocale !== e.target.value) {
        var data = {
          	"resourceType" : "Practitioner",
          	"communication": {
          		"language": {
          			"text": e.target.value
          		}
          	}
        }
        await (DataServiceSQL.updatePractitionnerReq_fhir(id, data))
         	.then((response) => {
	    		setCurrentLocale(data.communication.language.text);
          	})
          	.catch((e) => {
            	console.log(e);
          	});
		}
 	};

	let lang;
	let intlLocale;
	if (currentLocale==="fr-FR" || currentLocale.startsWith("fr")) {
	   lang = French;
	   intlLocale = "fr-FR";
	} 
	else if (currentLocale==="es-ES" || currentLocale.startsWith("es")) {
	   lang = Spanish;
	   intlLocale = "es-ES";
	}
	else {
	   lang = English;
	   intlLocale = "en";
	}

	// Poll backend health (SQL connection) at startup and every 60s - debug mode only
	useEffect(() => {
		if (process.env.REACT_APP_DEBUG !== 'true') return;
		const checkHealth = async () => {
			try {
				const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/health`);
				let data = null;
				try { data = await res.json(); } catch (_) { /* non-JSON body */ }
				if (res.ok && data && data.sql && data.sql.ok) {
					setBackendHealth({ checked: true, ok: true, message: "" });
				} else if (res.ok) {
					setBackendHealth({ checked: true, ok: false, message: (data && data.sql && data.sql.error) || "Database unavailable" });
				} else {
					setBackendHealth({ checked: true, ok: false, message: (data && data.sql && data.sql.error) || `Backend error ${res.status}` });
				}
			} catch (e) {
				setBackendHealth({ checked: true, ok: false, message: "Backend unreachable" });
			}
		};
		checkHealth();
		const interval = setInterval(checkHealth, 60000);
		return () => clearInterval(interval);
	}, []);



 	const historyLogout = (redirection) => {
 		historyObj.push(redirection);
 	}

	console.log(process.env.NODE_ENV)
	return (
		<IntlProvider
			locale={intlLocale}
			defaultLocale="en"
			messages={lang}
			onError={handleIntlError}
		>
			<div>
			    {process.env.REACT_APP_DEBUG === 'true' && backendHealth.checked && !backendHealth.ok && (
			        <div style={{
			            background: "#d9534f",
			            color: "white",
			            padding: "10px 16px",
			            textAlign: "center",
			            fontWeight: "bold",
			            position: "sticky",
			            top: 0,
			            zIndex: 9999
			        }}>
			            ⚠ Backend / Database connection issue: {backendHealth.message}
			        </div>
			    )}
			    <ReactKeycloakProvider initOptions={{ onLoad: 'login-required', checkLoginIframe: false }} authClient={keycloak}>
				<UserAccountGate
					setCurrentLocale={setCurrentLocale}
					setDisclaimerUsername={setDisclaimerUsername}
					setShowDisclaimer={setShowDisclaimer}
				/>
				<Routes>
			            	<Route
						path="/"
						element={
			            			<PrivateRoute>
			                			<SecuredPage path="/"/>
		            			<Home 
		            				currentLocale={currentLocale} 
		            				handleLocaleChange={handleLocaleChange} 
		            				currentFilter={currentFilter} 
		            				handleFilterChange={handleFilterChange} 
		            				currentView={currentView} 
		            				handleViewChange={handleViewChange} 
					            				historyLogout={historyLogout}
					            				screenSize={screenSize}
					            			/>
			            			</PrivateRoute>
			            		}
			            	/>
				</Routes>
			    </ReactKeycloakProvider>
				{showDisclaimer && disclaimerUsername && (
					<DisclaimerModal
						username={disclaimerUsername}
						onAccepted={() => setShowDisclaimer(false)}
					/>
				)}
			</div>
		</IntlProvider>	
	);
};

export default App;